import { Buffer } from "buffer";
import { Platform } from "react-native";

type BrowserVoiceHandlers = {
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (message: string) => void;
  onWarning?: (message: string) => void;
  onMuteChanged?: (muted: boolean) => void;
  onStatusChanged?: (status: string) => void;
};

export type BrowserVoiceSession = {
  disconnect: () => void;
  destroy: () => void;
  setMuted: (muted: boolean) => void;
  setSpeakerEnabled: (enabled: boolean) => void;
};

export type PreparedBrowserVoiceClient = {
  connect: (callSessionId: string) => Promise<BrowserVoiceSession>;
  destroy: () => void;
};

type MediaEvent =
  | {
      event: "connected";
      protocol: string;
      version: string;
    }
  | {
      event: "start";
      start: {
        streamSid: string;
      };
    }
  | {
      event: "media";
      media: {
        track?: "inbound";
        payload: string;
      };
      streamSid?: string;
    }
  | {
      event: "stop";
      streamSid?: string;
    };

type NativeAudioModule = typeof import("@speechmatics/expo-two-way-audio");
type EventSubscription = {
  remove?: () => void;
};

const frameSize = 160;
const targetSampleRate = 8_000;
const nativeSampleRate = 16_000;
const playbackLeadTimeSeconds = 0.05;
const hostedApiBaseUrl = "https://nativecall-worker.fhtjayson2025.workers.dev";

let cachedNativeAudioModule: NativeAudioModule | null = null;

function getBaseUrl() {
  if (process.env.EXPO_PUBLIC_API_BASE_URL) {
    return process.env.EXPO_PUBLIC_API_BASE_URL;
  }
  return hostedApiBaseUrl;
}

function getWsBaseUrl() {
  return getBaseUrl().replace("http://", "ws://").replace("https://", "wss://");
}

function buildClientMediaUrl(callSessionId: string) {
  return `${getWsBaseUrl()}/twilio/media/client/${encodeURIComponent(callSessionId)}`;
}

function buildPstnMediaUrl(callSessionId: string) {
  return `${getWsBaseUrl()}/twilio/media/pstn/${encodeURIComponent(callSessionId)}`;
}

function buildMediaUrl(callSessionId: string, leg: "client" | "pstn") {
  return leg === "pstn"
    ? buildPstnMediaUrl(callSessionId)
    : buildClientMediaUrl(callSessionId);
}

function getNativeAudioModule(): NativeAudioModule {
  if (Platform.OS === "web") {
    throw new Error("Native audio module requested on web.");
  }

  if (!cachedNativeAudioModule) {
    cachedNativeAudioModule =
      require("@speechmatics/expo-two-way-audio") as NativeAudioModule;
  }

  return cachedNativeAudioModule;
}

function clampPcmSample(sample: number) {
  return Math.max(-1, Math.min(1, sample));
}

function downsampleFloat32ToPcm16(input: Float32Array, inputSampleRate: number) {
  if (input.length === 0) {
    return new Int16Array(0);
  }

  if (inputSampleRate === targetSampleRate) {
    return Int16Array.from(input, (sample) =>
      Math.max(-32768, Math.min(32767, Math.round(clampPcmSample(sample) * 32767))),
    );
  }

  const ratio = inputSampleRate / targetSampleRate;
  const outputLength = Math.max(1, Math.round(input.length / ratio));
  const output = new Int16Array(outputLength);

  let outputIndex = 0;
  let inputIndex = 0;

  while (outputIndex < outputLength) {
    const nextInputIndex = Math.min(input.length, Math.round((outputIndex + 1) * ratio));
    let sum = 0;
    let count = 0;

    while (inputIndex < nextInputIndex) {
      sum += input[inputIndex] ?? 0;
      count += 1;
      inputIndex += 1;
    }

    const average = count > 0 ? sum / count : 0;
    output[outputIndex] = Math.max(
      -32768,
      Math.min(32767, Math.round(clampPcmSample(average) * 32767)),
    );
    outputIndex += 1;
  }

  return output;
}

function downsampleInt16(
  input: Int16Array,
  inputSampleRate: number,
  outputSampleRate: number,
) {
  if (input.length === 0) {
    return new Int16Array(0);
  }

  if (inputSampleRate === outputSampleRate) {
    return input;
  }

  const ratio = inputSampleRate / outputSampleRate;
  const outputLength = Math.max(1, Math.floor(input.length / ratio));
  const output = new Int16Array(outputLength);

  let outputIndex = 0;
  let inputIndex = 0;

  while (outputIndex < outputLength) {
    const nextInputIndex = Math.min(input.length, Math.round((outputIndex + 1) * ratio));
    let sum = 0;
    let count = 0;

    while (inputIndex < nextInputIndex) {
      sum += input[inputIndex] ?? 0;
      count += 1;
      inputIndex += 1;
    }

    output[outputIndex] = count > 0 ? Math.round(sum / count) : 0;
    outputIndex += 1;
  }

  return output;
}

function upsampleInt16(
  input: Int16Array,
  inputSampleRate: number,
  outputSampleRate: number,
) {
  if (input.length === 0) {
    return new Int16Array(0);
  }

  if (inputSampleRate === outputSampleRate) {
    return input;
  }

  const ratio = outputSampleRate / inputSampleRate;
  const outputLength = Math.max(1, Math.round(input.length * ratio));
  const output = new Int16Array(outputLength);

  for (let index = 0; index < outputLength; index += 1) {
    const position = index / ratio;
    const leftIndex = Math.floor(position);
    const rightIndex = Math.min(input.length - 1, leftIndex + 1);
    const interpolation = position - leftIndex;
    const left = input[leftIndex] ?? 0;
    const right = input[rightIndex] ?? left;
    output[index] = Math.round(left + (right - left) * interpolation);
  }

  return output;
}

function linearToMulawSample(sample: number) {
  const mulawBias = 0x84;
  const mulawClip = 32635;

  let pcm = sample;
  let sign = 0;

  if (pcm < 0) {
    sign = 0x80;
    pcm = -pcm;
  }

  pcm = Math.min(pcm, mulawClip);
  pcm += mulawBias;

  let exponent = 7;
  for (let mask = 0x4000; (pcm & mask) === 0 && exponent > 0; mask >>= 1) {
    exponent -= 1;
  }

  const mantissa = (pcm >> (exponent + 3)) & 0x0f;
  return (~(sign | (exponent << 4) | mantissa)) & 0xff;
}

function mulawToLinearSample(byte: number) {
  const mulawBias = 0x84;
  const value = (~byte) & 0xff;
  const sign = value & 0x80;
  const exponent = (value >> 4) & 0x07;
  const mantissa = value & 0x0f;
  const magnitude = (((mantissa << 3) + mulawBias) << exponent) - mulawBias;
  return sign ? -magnitude : magnitude;
}

function encodeMulawBase64(samples: Int16Array) {
  const bytes = new Uint8Array(samples.length);
  for (let index = 0; index < samples.length; index += 1) {
    bytes[index] = linearToMulawSample(samples[index] ?? 0);
  }
  return Buffer.from(bytes).toString("base64");
}

function decodeMulawBase64(payload: string) {
  const bytes = new Uint8Array(Buffer.from(payload, "base64"));
  const output = new Int16Array(bytes.length);
  for (let index = 0; index < bytes.length; index += 1) {
    output[index] = mulawToLinearSample(bytes[index] ?? 0);
  }
  return output;
}

function splitIntoMulawFrames(samples: Int16Array) {
  const frames: string[] = [];

  for (let index = 0; index + frameSize <= samples.length; index += frameSize) {
    frames.push(encodeMulawBase64(samples.subarray(index, index + frameSize)));
  }

  const remainder = samples.length % frameSize;
  const leftover =
    remainder > 0 ? samples.slice(samples.length - remainder, samples.length) : new Int16Array(0);

  return {
    frames,
    leftover,
  };
}

function int16ArrayFromBytes(bytes: Uint8Array) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const output = new Int16Array(bytes.byteLength / 2);

  for (let index = 0; index < output.length; index += 1) {
    output[index] = view.getInt16(index * 2, true);
  }

  return output;
}

function int16ArrayToBytes(samples: Int16Array) {
  const bytes = new Uint8Array(samples.length * 2);
  const view = new DataView(bytes.buffer);

  for (let index = 0; index < samples.length; index += 1) {
    view.setInt16(index * 2, samples[index] ?? 0, true);
  }

  return bytes;
}

function createAudioContext() {
  const browserWindow = window as typeof window & {
    webkitAudioContext?: typeof AudioContext;
  };
  const AudioContextCtor = window.AudioContext ?? browserWindow.webkitAudioContext;
  if (!AudioContextCtor) {
    throw new Error("Web Audio is not available in this browser.");
  }

  return new AudioContextCtor();
}

async function prepareWebVoiceClient(input: {
  handlers?: BrowserVoiceHandlers;
  leg?: "client" | "pstn";
}): Promise<PreparedBrowserVoiceClient> {
  if (typeof window === "undefined") {
    throw new Error("Browser voice calling is only available on web.");
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("Browser audio capture is not available in this environment.");
  }

  input.handlers?.onStatusChanged?.("requesting-microphone");

  let localStream: MediaStream;
  try {
    localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? `Microphone access failed: ${error.message}`
        : "Microphone access failed.",
    );
  }

  input.handlers?.onStatusChanged?.("creating-browser-device");

  const audioContext = createAudioContext();
  const inputSource = audioContext.createMediaStreamSource(localStream);
  const processor = audioContext.createScriptProcessor(4096, 1, 1);
  const playbackGain = audioContext.createGain();
  const playbackDestination = audioContext.createGain();

  playbackGain.gain.value = 1;
  playbackDestination.gain.value = 0;
  playbackGain.connect(audioContext.destination);
  playbackDestination.connect(audioContext.destination);
  inputSource.connect(processor);
  processor.connect(playbackDestination);

  let destroyed = false;
  let muted = false;
  let speakerEnabled = true;
  let websocket: WebSocket | null = null;
  let streamSid: string | null = null;
  let remoteReady = false;
  let playbackCursor = 0;
  let sampleRemainder = new Int16Array(0);

  const destroy = () => {
    if (destroyed) {
      return;
    }

    destroyed = true;
    try {
      if (websocket?.readyState === WebSocket.OPEN) {
        websocket.send(
          JSON.stringify({
            event: "stop",
            ...(streamSid ? { streamSid } : {}),
          } satisfies MediaEvent),
        );
      }
    } catch {
      // Ignore close-time websocket send failures.
    }

    websocket?.close();
    websocket = null;
    processor.disconnect();
    inputSource.disconnect();
    playbackGain.disconnect();
    playbackDestination.disconnect();
    localStream.getTracks().forEach((track) => track.stop());
    void audioContext.close().catch(() => {});
  };

  const schedulePlayback = (payload: string) => {
    const samples = decodeMulawBase64(payload);
    if (samples.length === 0) {
      return;
    }

    const floatAudio = new Float32Array(samples.length);
    for (let index = 0; index < samples.length; index += 1) {
      floatAudio[index] = (samples[index] ?? 0) / 32768;
    }

    const buffer = audioContext.createBuffer(1, floatAudio.length, targetSampleRate);
    buffer.getChannelData(0).set(floatAudio);

    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(playbackGain);

    playbackGain.gain.value = speakerEnabled ? 1 : 0;
    const now = audioContext.currentTime + playbackLeadTimeSeconds;
    const startAt = Math.max(now, playbackCursor);
    source.start(startAt);
    playbackCursor = startAt + buffer.duration;
  };

  processor.onaudioprocess = (event) => {
    if (destroyed || muted || !websocket || websocket.readyState !== WebSocket.OPEN) {
      return;
    }

    const inputSamples = event.inputBuffer.getChannelData(0);
    const pcm16 = downsampleFloat32ToPcm16(inputSamples, audioContext.sampleRate);
    if (pcm16.length === 0) {
      return;
    }

    const merged = new Int16Array(sampleRemainder.length + pcm16.length);
    merged.set(sampleRemainder, 0);
    merged.set(pcm16, sampleRemainder.length);

    const { frames, leftover } = splitIntoMulawFrames(merged);
    sampleRemainder = leftover;

    for (const payload of frames) {
      websocket.send(
        JSON.stringify({
          event: "media",
          media: {
            track: "inbound",
            payload,
          },
          ...(streamSid ? { streamSid } : {}),
        } satisfies MediaEvent),
      );
    }
  };

  return {
    connect: async (callSessionId: string) => {
      if (destroyed) {
        throw new Error("Browser voice client has already been destroyed.");
      }

      input.handlers?.onStatusChanged?.("connecting-browser-leg");
      await audioContext.resume();

      const leg = input.leg ?? "client";
      const nextStreamSid = `browser-${leg}-${callSessionId}-${crypto.randomUUID()}`;
      streamSid = nextStreamSid;

      const socket = new WebSocket(buildMediaUrl(callSessionId, leg));
      websocket = socket;

      await new Promise<void>((resolve, reject) => {
        const timeout = window.setTimeout(() => {
          reject(new Error("Browser voice leg did not connect in time."));
        }, 15_000);

        const cleanup = () => {
          window.clearTimeout(timeout);
          socket.removeEventListener("open", handleOpen);
          socket.removeEventListener("error", handleError);
          socket.removeEventListener("close", handleCloseBeforeOpen);
        };

        const handleOpen = () => {
          cleanup();
          resolve();
        };

        const handleError = () => {
          cleanup();
          reject(new Error("Browser media websocket failed to open."));
        };

        const handleCloseBeforeOpen = () => {
          cleanup();
          reject(new Error("Browser media websocket closed before it connected."));
        };

        socket.addEventListener("open", handleOpen);
        socket.addEventListener("error", handleError);
        socket.addEventListener("close", handleCloseBeforeOpen);
      }).catch((error) => {
        destroy();
        throw error;
      });

      socket.send(
        JSON.stringify({
          event: "connected",
          protocol: "Call",
          version: "1.0.0",
        } satisfies MediaEvent),
      );
      socket.send(
        JSON.stringify({
          event: "start",
          start: {
            streamSid: nextStreamSid,
          },
        } satisfies MediaEvent),
      );

      input.handlers?.onStatusChanged?.("browser-leg-created");
      input.handlers?.onConnected?.();

      socket.addEventListener("message", (message) => {
        try {
          const event = JSON.parse(String(message.data)) as MediaEvent;

          if (event.event === "media" && event.media?.payload) {
            schedulePlayback(event.media.payload);
            if (!remoteReady) {
              remoteReady = true;
              input.handlers?.onStatusChanged?.("remote-audio-ready");
            }
            return;
          }

          if (event.event === "stop") {
            input.handlers?.onStatusChanged?.("browser-leg-disconnected");
            input.handlers?.onDisconnected?.();
          }
        } catch (error) {
          input.handlers?.onWarning?.(
            error instanceof Error ? error.message : "Unable to parse browser media event.",
          );
        }
      });

      socket.addEventListener("close", () => {
        if (!destroyed) {
          input.handlers?.onStatusChanged?.("browser-leg-disconnected");
          input.handlers?.onDisconnected?.();
        }
        destroy();
      });

      socket.addEventListener("error", () => {
        input.handlers?.onStatusChanged?.("browser-leg-error");
        input.handlers?.onError?.("Browser media websocket failed.");
      });

      return {
        disconnect: () => {
          destroy();
        },
        destroy,
        setMuted: (nextMuted) => {
          muted = nextMuted;
          input.handlers?.onMuteChanged?.(nextMuted);
        },
        setSpeakerEnabled: (enabled) => {
          speakerEnabled = enabled;
          playbackGain.gain.value = enabled ? 1 : 0;
        },
      };
    },
    destroy,
  };
}

async function prepareNativeVoiceClient(input: {
  handlers?: BrowserVoiceHandlers;
  leg?: "client" | "pstn";
}): Promise<PreparedBrowserVoiceClient> {
  const nativeAudio = getNativeAudioModule();

  input.handlers?.onStatusChanged?.("requesting-microphone");
  const permission = await nativeAudio.requestMicrophonePermissionsAsync();
  if (!permission.granted) {
    throw new Error("Microphone access is required to place a translated call.");
  }

  input.handlers?.onStatusChanged?.("creating-browser-device");
  await nativeAudio.initialize();
  nativeAudio.toggleRecording(false);

  let destroyed = false;
  let muted = false;
  let speakerEnabled = true;
  let websocket: WebSocket | null = null;
  let streamSid: string | null = null;
  let remoteReady = false;
  let sampleRemainder = new Int16Array(0);
  const subscriptions: EventSubscription[] = [];

  const destroy = () => {
    if (destroyed) {
      return;
    }

    destroyed = true;
    try {
      nativeAudio.toggleRecording(false);
    } catch {}

    try {
      if (websocket?.readyState === WebSocket.OPEN) {
        websocket.send(
          JSON.stringify({
            event: "stop",
            ...(streamSid ? { streamSid } : {}),
          } satisfies MediaEvent),
        );
      }
    } catch {
      // Ignore websocket close failures during teardown.
    }

    websocket?.close();
    websocket = null;
    subscriptions.forEach((subscription) => subscription.remove?.());
    subscriptions.length = 0;

    try {
      nativeAudio.tearDown();
    } catch {}
  };

  subscriptions.push(
    nativeAudio.addExpoTwoWayAudioEventListener("onMicrophoneData", (event) => {
      if (destroyed || muted || !websocket || websocket.readyState !== WebSocket.OPEN) {
        return;
      }

      const inputBytes = event.data instanceof Uint8Array ? event.data : new Uint8Array(event.data);
      const pcm16 = int16ArrayFromBytes(inputBytes);
      if (pcm16.length === 0) {
        return;
      }

      const downsampled = downsampleInt16(pcm16, nativeSampleRate, targetSampleRate);
      const merged = new Int16Array(sampleRemainder.length + downsampled.length);
      merged.set(sampleRemainder, 0);
      merged.set(downsampled, sampleRemainder.length);

      const { frames, leftover } = splitIntoMulawFrames(merged);
      sampleRemainder = leftover;

      for (const payload of frames) {
        websocket.send(
          JSON.stringify({
            event: "media",
            media: {
              track: "inbound",
              payload,
            },
            ...(streamSid ? { streamSid } : {}),
          } satisfies MediaEvent),
        );
      }
    }),
  );

  subscriptions.push(
    nativeAudio.addExpoTwoWayAudioEventListener("onAudioInterruption", (event) => {
      input.handlers?.onWarning?.(`Audio interruption: ${event.data}`);
    }),
  );

  return {
    connect: async (callSessionId: string) => {
      if (destroyed) {
        throw new Error("Native voice client has already been destroyed.");
      }

      input.handlers?.onStatusChanged?.("connecting-browser-leg");
      const leg = input.leg ?? "client";
      const nextStreamSid = `native-${leg}-${callSessionId}-${Date.now()}`;
      streamSid = nextStreamSid;

      const socket = new WebSocket(buildMediaUrl(callSessionId, leg));
      websocket = socket;

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("Native voice leg did not connect in time."));
        }, 15_000);

        const cleanup = () => {
          clearTimeout(timeout);
          socket.removeEventListener("open", handleOpen);
          socket.removeEventListener("error", handleError);
          socket.removeEventListener("close", handleCloseBeforeOpen);
        };

        const handleOpen = () => {
          cleanup();
          resolve();
        };

        const handleError = () => {
          cleanup();
          reject(new Error("Native media websocket failed to open."));
        };

        const handleCloseBeforeOpen = () => {
          cleanup();
          reject(new Error("Native media websocket closed before it connected."));
        };

        socket.addEventListener("open", handleOpen);
        socket.addEventListener("error", handleError);
        socket.addEventListener("close", handleCloseBeforeOpen);
      }).catch((error) => {
        destroy();
        throw error;
      });

      socket.send(
        JSON.stringify({
          event: "connected",
          protocol: "Call",
          version: "1.0.0",
        } satisfies MediaEvent),
      );
      socket.send(
        JSON.stringify({
          event: "start",
          start: {
            streamSid: nextStreamSid,
          },
        } satisfies MediaEvent),
      );

      nativeAudio.toggleRecording(!muted);
      input.handlers?.onStatusChanged?.("browser-leg-created");
      input.handlers?.onConnected?.();

      socket.addEventListener("message", (message) => {
        try {
          const event = JSON.parse(String(message.data)) as MediaEvent;

          if (event.event === "media" && event.media?.payload) {
            if (speakerEnabled) {
              const downsampled = decodeMulawBase64(event.media.payload);
              const upsampled = upsampleInt16(
                downsampled,
                targetSampleRate,
                nativeSampleRate,
              );
              nativeAudio.playPCMData(int16ArrayToBytes(upsampled));
            }

            if (!remoteReady) {
              remoteReady = true;
              input.handlers?.onStatusChanged?.("remote-audio-ready");
            }
            return;
          }

          if (event.event === "stop") {
            input.handlers?.onStatusChanged?.("browser-leg-disconnected");
            input.handlers?.onDisconnected?.();
          }
        } catch (error) {
          input.handlers?.onWarning?.(
            error instanceof Error ? error.message : "Unable to parse native media event.",
          );
        }
      });

      let lastCloseInfo: string | null = null;

      socket.addEventListener("close", (event) => {
        const closeEvent = event as CloseEvent;
        lastCloseInfo = `code=${closeEvent.code} reason=${closeEvent.reason || "none"} wasClean=${closeEvent.wasClean}`;
        if (!destroyed) {
          input.handlers?.onStatusChanged?.("browser-leg-disconnected");
          input.handlers?.onDisconnected?.();
        }
        destroy();
      });

      socket.addEventListener("error", () => {
        // Per WebSocket spec, `error` fires before `close` on abnormal
        // termination. Defer so the close handler populates `lastCloseInfo`
        // first; without this the close code/reason is always missing from
        // the diagnostic.
        input.handlers?.onStatusChanged?.("browser-leg-error");
        setTimeout(() => {
          input.handlers?.onError?.(
            `Native media websocket failed. readyState=${socket.readyState} streamSid=${nextStreamSid}${lastCloseInfo ? " " + lastCloseInfo : ""}`,
          );
        }, 0);
      });

      return {
        disconnect: () => {
          destroy();
        },
        destroy,
        setMuted: (nextMuted) => {
          muted = nextMuted;
          nativeAudio.toggleRecording(!nextMuted);
          input.handlers?.onMuteChanged?.(nextMuted);
        },
        setSpeakerEnabled: (enabled) => {
          speakerEnabled = enabled;
        },
      };
    },
    destroy,
  };
}

export async function prepareBrowserVoiceClient(input: {
  userId: string;
  identity: string;
  handlers?: BrowserVoiceHandlers;
  leg?: "client" | "pstn";
}): Promise<PreparedBrowserVoiceClient> {
  if (Platform.OS === "web") {
    return prepareWebVoiceClient(input);
  }

  return prepareNativeVoiceClient(input);
}

export async function connectBrowserVoiceCall(input: {
  userId: string;
  identity: string;
  callSessionId: string;
  handlers?: BrowserVoiceHandlers;
  leg?: "client" | "pstn";
}) {
  const client = await prepareBrowserVoiceClient({
    userId: input.userId,
    identity: input.identity,
    handlers: input.handlers,
    leg: input.leg,
  });

  try {
    return await client.connect(input.callSessionId);
  } catch (error) {
    client.destroy();
    throw error;
  }
}
